import { useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useVerifyDestination, type DestinationScope } from '@/hooks/useNotifications';
import { Button } from '@/components/ui/button';
import { config } from '@/config';

/**
 * Landing page for the confirmation link in a destination verification email
 * (`/notifications/verify-destination?id=…&token=…&scope=…`).
 *
 * All three parameters are load-bearing. The token proves control of the address; the
 * id says which destination it proves it for, because the API never discloses a
 * verification token and so a token alone cannot be traded for the row it belongs to;
 * and the scope says which endpoint can accept the proof, since personal and shared
 * destinations are verified through different ones with different authorization.
 * A link missing the id or the token is dead on arrival, and says so rather than
 * spinning.
 */
export default function VerifyNotificationDestination() {
  const [params] = useSearchParams();
  const id = params.get('id') ?? '';
  const token = params.get('token') ?? '';
  // Personal when absent: links minted before the scope existed are all personal, and
  // the organization surface had no working verify at all until it was added.
  const scope: DestinationScope = params.get('scope') === 'org' ? 'org' : 'personal';
  const verify = useVerifyDestination();
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current || !id || !token) return;
    fired.current = true;
    verify.mutate({ id, token, scope });
  }, [id, token, scope, verify]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="bg-card border border-border rounded-lg p-8 max-w-md w-full text-center space-y-4">
        {!id || !token ? (
          <>
            <XCircle className="h-10 w-10 text-destructive mx-auto" />
            <h1 className="text-lg font-semibold">Incomplete confirmation link</h1>
            <p className="text-sm text-muted-foreground">
              This link is missing part of what it needs. Open the one in your email again, or add the address
              afresh to get a new link.
            </p>
          </>
        ) : verify.isPending || verify.isIdle ? (
          <>
            <Loader2 className="h-10 w-10 text-muted-foreground mx-auto animate-spin" />
            <h1 className="text-lg font-semibold">Confirming this address…</h1>
          </>
        ) : verify.isSuccess ? (
          <>
            <CheckCircle2 className="h-10 w-10 text-sev-4 mx-auto" />
            <h1 className="text-lg font-semibold">Address confirmed</h1>
            <p className="text-sm text-muted-foreground">
              {config.appFullName} notifications can now be sent here. Point a notification rule at it to start
              receiving them.
            </p>
          </>
        ) : (
          <>
            <XCircle className="h-10 w-10 text-destructive mx-auto" />
            <h1 className="text-lg font-semibold">Couldn't confirm this address</h1>
            <p className="text-sm text-muted-foreground">
              The link may have already been used, or the address may have been removed since it was sent.
            </p>
          </>
        )}
        <Button asChild variant="outline">
          <Link to="/notifications/destinations">Go to notification settings</Link>
        </Button>
      </div>
    </div>
  );
}
